import React, { Fragment } from 'react';

import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import type { ExtensionHandler } from '@atlaskit/editor-common/extensions';

import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import { EXTENSION_KEY } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
	RENDERER,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { InlineExternalImage } from '@confluence/inline-external-image/entry-points/InlineExternalImage.ts';
import type { InlineExternalImageParams } from '@confluence/inline-external-image/entry-points/InlineExternalImage.ts';

import type { InlineImageParams, ExtensionParamsType } from '../extensionHandlerTypes.ts';
import { InvalidExtensionLoader as InvalidExtension } from '../InvalidExtension/InvalidExtensionLoader.tsx';
import { InlineImage } from '../InlineImage/index.ts';
import { LegacyContentExtensionHandler } from '../LegacyContentExtension/index.ts';

export const getMigrationExtHandlerForRenderer = ({
	contentId,
	contextProviders,
}): ExtensionHandler<ExtensionParamsType> =>
	createMacroExtensionHandler((extension) => {
		if (!extension) {
			return null;
		}

		const { extensionKey, parameters } = extension;
		const { ClassicEditorContextProvider = ({ children }) => children } = contextProviders;
		const attributes = getMacroAttributesFromADFNode(extension);
		const defaultProps = {
			contentId,
			mode: RENDERER,
			extensionKey,
			attributes,
			node: extension,
		};

		let implementation;

		switch (extensionKey) {
			case EXTENSION_KEY.INVALID:
				// extension is rendered outside of React DOM tree and require access to the ClassicEditorContext
				implementation = (
					<ClassicEditorContextProvider>
						<InvalidExtension parameters={parameters} {...defaultProps} />
					</ClassicEditorContextProvider>
				);
				break;

			case EXTENSION_KEY.INLINE_EXTERNAL_IMAGE:
				const inlineExternalImageProps = parameters as InlineExternalImageParams;
				implementation = <InlineExternalImage {...inlineExternalImageProps} {...defaultProps} />;
				break;

			case EXTENSION_KEY.INLINE_MEDIA_IMAGE:
				const inlineMediaImageProps = parameters as InlineImageParams;
				const { link } = parameters;
				implementation = <InlineImage {...inlineMediaImageProps} {...defaultProps} link={link} />;
				break;

			case EXTENSION_KEY.LEGACY_CONTENT:
				implementation = <LegacyContentExtensionHandler {...defaultProps} />;
				break;
		}

		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(RENDERER, extension)}
					mode={RENDERER}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
					contentId={contentId}
				/>
				<RendererActionsContext>{implementation}</RendererActionsContext>
			</Fragment>
		);
	});
